import { useState, useCallback, useEffect, Fragment } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../utils/api';

interface HighlightSegment {
  text: string;
  highlighted: boolean;
}

interface SearchResult {
  id: number;
  url: string;
  title: string;
  title_segments: HighlightSegment[];
  excerpt_segments: HighlightSegment[];
  status: string;
  status_label: string;
  status_variant: 'positive' | 'in_progress' | 'on_hold' | null;
  date: string | null;
  publication_name: string | null;
  byline: string | null;
}

interface AggregationOption {
  value: string;
  label: string;
}

interface AggregationFilter {
  /** Aggregation key from ES, e.g. "statuses", "contributors", "publications". */
  name: string;
  /** URL query-param name the FE submits, e.g. "status" (name.singularize). */
  param: string;
  /** Prompt label rendered as the first <option>, e.g. "All statuses". */
  label: string;
  /** Currently-selected value (echoed from params), or null. */
  selected: string | null;
  options: AggregationOption[];
}

interface SearchResponse {
  query: string;
  simple_search_enabled: boolean;
  current_mode: 'simple' | 'advanced';
  total_count: number;
  current_page: number;
  total_pages: number;
  per_page: number;
  sort_by: string;
  sort_direction: string;
  filters: AggregationFilter[];
  results: SearchResult[];
}

/**
 * Sort options — hardcoded to match the legacy HAML's `sort_options` array.
 * Each value is "<sort_by>|<sort_direction>" so a single <select> can drive
 * both BE params in one change.
 */
const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: 'relevance|desc', label: 'Relevance' },
  { value: 'newest|desc', label: 'Recently Published' },
  { value: 'oldest|asc', label: 'Oldest Published' },
  { value: 'recently_updated|desc', label: 'Recently Updated' },
];

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const ContentWrapper = styled.div`
  flex: 1;
  max-width: 720px;
  width: 100%;
  margin: 0 auto;
  padding: 32px 20px 60px;
`;

const Heading = styled.h1`
  font-size: 24px;
  font-weight: 300;
  color: #333;
  margin: 0 0 20px;
  text-align: center;
  line-height: 1.3;
  strong { font-weight: 600; }
`;

const NoResults = styled.h3`
  font-size: 18px;
  font-weight: 300;
  color: #666;
  margin: 0 0 20px;
  text-align: center;
  line-height: 1.4;
`;

const SearchForm = styled.form`
  display: flex;
  gap: 0;
  margin-bottom: 20px;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  font-size: 14px;
  border: 1px solid #ccc;
  border-right: none;
  border-radius: 3px 0 0 3px;
  box-sizing: border-box;
  &:focus { outline: none; border-color: #00a6c2; }
`;

const SearchButton = styled.button`
  padding: 8px 24px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  background: #00a6c2;
  color: white;
  border: 1px solid #00a6c2;
  border-radius: 0 3px 3px 0;
  cursor: pointer;
  &:hover { background: #0094ab; border-color: #0094ab; }
  &:disabled { background: #ccc; border-color: #ccc; cursor: not-allowed; }
`;

const ResultsList = styled.div`
  border-top: 1px solid #e5e5e5;
`;

const ResultCard = styled.a`
  display: block;
  padding: 14px 0;
  border-bottom: 1px solid #e5e5e5;
  text-decoration: none;
  color: inherit;
`;

const ResultTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
`;

const StatusBadge = styled.span<{ $variant: SearchResult['status_variant'] }>`
  display: inline-block;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: white;
  border-radius: 2px;
  background: ${({ $variant }) => {
    switch ($variant) {
      case 'positive':
        return '#5cb85c';
      case 'in_progress':
        return '#f0ad4e';
      case 'on_hold':
        return '#d9534f';
      default:
        return '#999';
    }
  }};
`;

const DateText = styled.span`
  font-size: 12px;
  color: #999;
`;

const ResultTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #00a6c2;
  margin-bottom: 4px;
  mark { background: #ffeb3b; padding: 0 2px; color: inherit; }
`;

const ResultMeta = styled.div`
  font-size: 13px;
  color: #666;
  margin-bottom: 4px;
  .dot { margin: 0 6px; color: #bbb; }
`;

const ResultExcerpt = styled.p`
  font-size: 13px;
  color: #555;
  line-height: 1.5;
  margin: 0;
  mark { background: #ffeb3b; padding: 0 2px; color: inherit; }
`;

const Pagination = styled.nav`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  margin: 24px 0 0;
  font-size: 13px;
`;

const PageLink = styled.a<{ $active?: boolean; $disabled?: boolean }>`
  padding: 6px 12px;
  flex-shrink: 0;
  white-space: nowrap;
  color: ${({ $active, $disabled }) => ($disabled ? '#ccc' : $active ? '#fff' : '#00a6c2')};
  background: ${({ $active }) => ($active ? '#00a6c2' : 'transparent')};
  border: 1px solid ${({ $active }) => ($active ? '#00a6c2' : 'transparent')};
  border-radius: 3px;
  text-decoration: none;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
  &:hover {
    background: ${({ $active, $disabled }) => ($disabled ? 'transparent' : $active ? '#0094ab' : '#f5f5f5')};
  }
`;

const Ellipsis = styled.span`
  padding: 6px 4px;
  flex-shrink: 0;
  color: #999;
`;

const ModeRow = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 16px;
  font-size: 12px;
  color: #666;
  align-items: center;
`;

/**
 * Filter row — one <select> per active aggregation plus the always-visible
 * sort dropdown. Legacy used select2 with a "filters" theme; native <select>
 * is the simplest cross-browser parity path and matches the rest of
 * platform-web's migrated forms.
 */
const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`;

const FilterSelect = styled.select`
  /* Suppress browser-default <select> chrome (oversized fonts, native chevron
     glyph that varies across Chrome/Firefox/Safari) so we can render a
     consistent, compact dropdown matching the legacy select2-themed filters
     on prod. Lisa Farino reported (sheet row 67) that the canary search
     filters showed no dropdown affordance, were oversized, and stacked
     vertically — all symptoms of unstyled native <select>. */
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  padding: 6px 28px 6px 10px;
  font-size: 13px;
  color: #333;
  background-color: #fff;
  /* Inline SVG chevron (down-arrow) — sized to match the 28px right padding
     so the arrow sits cleanly in the reserved space. */
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path fill='%23666' d='M0 0l5 6 5-6z'/></svg>");
  background-repeat: no-repeat;
  background-position: right 10px center;
  border: 1px solid #ccc;
  border-radius: 3px;
  cursor: pointer;
  /* Bound the width so the four filters stay on a single row in the
     viewport-typical 1280-1440px range and don't blow out to fit
     long contributor/publication names. */
  min-width: 140px;
  max-width: 220px;
  &:focus {
    outline: none;
    border-color: #00a6c2;
  }
`;

const ModeToggle = styled.button<{ $active: boolean }>`
  padding: 4px 10px;
  border: 1px solid ${p => p.$active ? '#00a6c2' : '#ddd'};
  border-radius: 3px;
  background: ${p => p.$active ? '#00a6c2' : '#f8f9fa'};
  color: ${p => p.$active ? '#fff' : '#333'};
  cursor: pointer;
  font-size: 12px;
  &:hover { background: ${p => p.$active ? '#0094ab' : '#e9ecef'}; }
`;

const Loading = styled.div`
  text-align: center;
  padding: 40px;
  color: #999;
  font-size: 13px;
`;

function formatDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Render an array of { text, highlighted } segments as plain React nodes.
 * No HTML injection — each segment becomes a text node or a <mark> element.
 */
function renderSegments(segments: HighlightSegment[] | undefined, fallback?: string) {
  if (!segments || segments.length === 0) return fallback ?? null;
  return segments.map((seg, i) => (
    <Fragment key={i}>
      {seg.highlighted ? <mark>{seg.text}</mark> : seg.text}
    </Fragment>
  ));
}

/**
 * SearchPage — mirrors legacy /search: centered "We found N stories..."
 * header, result cards with status badge, date, highlighted title, byline +
 * publication, highlighted excerpt, and pagination.
 *
 * BE returns per-result: status, status_variant, date, publication_name,
 * byline, title_segments, excerpt_segments (both arrays of
 * { text, highlighted } so highlighting renders without raw HTML).
 */
/** Param names the BE understands for aggregation filters. */
const FILTER_PARAMS = ['status', 'contributor', 'publication'] as const;
type FilterParamName = (typeof FILTER_PARAMS)[number];

/**
 * Compact the page range to first / current±1 / last with '...' gaps so the
 * control stays readable when a broad search yields many pages (e.g. 50).
 * Mirrors the established idiom in
 * command-center/components/WidgetPagination.tsx. Returns page numbers plus
 * '...' markers (rendered as non-clickable spans).
 */
function getVisiblePages(current: number, total: number): (number | string)[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | string)[] = [];
  pages.push(1);
  if (current > 3) pages.push('...');
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
    pages.push(i);
  }
  if (current < total - 2) pages.push('...');
  pages.push(total);
  return pages;
}

function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('query') || searchParams.get('q') || '';
  const initialPage = parseInt(searchParams.get('page') || '1', 10) || 1;
  const initialSort = `${searchParams.get('sort_by') || 'relevance'}|${
    searchParams.get('sort_direction') || 'desc'
  }`;
  const initialFilters: Record<FilterParamName, string> = {
    status: searchParams.get('status') || '',
    contributor: searchParams.get('contributor') || '',
    publication: searchParams.get('publication') || '',
  };

  const [query, setQuery] = useState(initialQuery);
  const [inputQuery, setInputQuery] = useState(initialQuery);
  const [mode, setMode] = useState<'simple' | 'advanced'>(
    (searchParams.get('mode') as 'simple' | 'advanced') || 'advanced'
  );
  const [page, setPage] = useState(initialPage);
  const [sortCombined, setSortCombined] = useState<string>(initialSort);
  const [filterValues, setFilterValues] =
    useState<Record<FilterParamName, string>>(initialFilters);
  const [response, setResponse] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const doSearch = useCallback(
    async (
      q: string,
      m: string,
      pg: number,
      sort: string,
      filters: Record<FilterParamName, string>
    ) => {
      if (!q.trim()) return;
      setLoading(true);
      const [sort_by, sort_direction] = sort.split('|');
      // Build params object: only include filter params that are set, matching
      // legacy behaviour where absent params are omitted entirely.
      const params: Record<string, string | number> = {
        query: q,
        mode: m,
        page: pg,
        sort_by,
        sort_direction,
      };
      FILTER_PARAMS.forEach((key) => {
        if (filters[key]) params[key] = filters[key];
      });
      try {
        const { data } = await api.get<SearchResponse>('/search.json', { params });
        setResponse(data);
      } catch (err) {
        console.error('Search failed:', err);
        setResponse(null);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (query) {
      doSearch(query, mode, page, sortCombined, filterValues);
    }
  }, [query, mode, page, sortCombined, filterValues, doSearch]);

  // Single source of truth for URL sync: rebuild searchParams from current
  // state so /search?query=…&status=…&sort_by=… always reflects what the
  // user sees. Omit empty filter params so URLs stay clean.
  const syncUrl = useCallback(
    (
      q: string,
      m: string,
      pg: number,
      sort: string,
      filters: Record<FilterParamName, string>
    ) => {
      const [sort_by, sort_direction] = sort.split('|');
      const next: Record<string, string> = {
        query: q,
        mode: m,
        page: String(pg),
        sort_by,
        sort_direction,
      };
      FILTER_PARAMS.forEach((key) => {
        if (filters[key]) next[key] = filters[key];
      });
      setSearchParams(next);
    },
    [setSearchParams]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = inputQuery.trim();
    if (!trimmed) return;
    setQuery(trimmed);
    setPage(1);
    syncUrl(trimmed, mode, 1, sortCombined, filterValues);
  };

  const handleModeChange = (newMode: 'simple' | 'advanced') => {
    setMode(newMode);
    setPage(1);
    syncUrl(query, newMode, 1, sortCombined, filterValues);
  };

  const handlePageChange = (newPage: number) => {
    if (!response) return;
    if (newPage < 1 || newPage > response.total_pages) return;
    setPage(newPage);
    syncUrl(query, mode, newPage, sortCombined, filterValues);
    window.scrollTo(0, 0);
  };

  const handleSortChange = (newSort: string) => {
    setSortCombined(newSort);
    setPage(1);
    syncUrl(query, mode, 1, newSort, filterValues);
  };

  const handleFilterChange = (param: FilterParamName, value: string) => {
    const next = { ...filterValues, [param]: value };
    setFilterValues(next);
    setPage(1);
    syncUrl(query, mode, 1, sortCombined, next);
  };

  useEffect(() => {
    document.title = response?.total_count
      ? `Search results : Contently`
      : 'Search : Contently';
    return () => { document.title = 'Contently'; };
  }, [response]);

  const total = response?.total_count ?? 0;
  const hasResults = total > 0;

  return (
    <PageContainer>
      <ContentWrapper>
        {hasResults ? (
          <Heading>
            We found <strong>{total}</strong> stor{total === 1 ? 'y' : 'ies'} matching your search
          </Heading>
        ) : response && query ? (
          <NoResults>
            Sorry, we couldn&rsquo;t find anything that matches &lsquo;{query}&rsquo;. Please give it another try.
          </NoResults>
        ) : (
          <Heading>Search</Heading>
        )}

        <SearchForm onSubmit={handleSubmit}>
          <SearchInput
            type="text"
            value={inputQuery}
            onChange={(e) => setInputQuery(e.target.value)}
            placeholder="Search stories..."
            autoFocus
          />
          <SearchButton type="submit" disabled={!inputQuery.trim() || loading}>
            Find
          </SearchButton>
        </SearchForm>

        {response?.simple_search_enabled && (
          <ModeRow>
            <span>Mode:</span>
            <ModeToggle $active={mode === 'simple'} onClick={() => handleModeChange('simple')}>
              Simple
            </ModeToggle>
            <ModeToggle $active={mode === 'advanced'} onClick={() => handleModeChange('advanced')}>
              Advanced
            </ModeToggle>
          </ModeRow>
        )}

        {/* Filter + sort row — only rendered once we have a response so the
            sort dropdown doesn't flash before the first result set loads.
            Aggregation dropdowns are driven by whatever buckets the current
            result set produced, matching the legacy HAML's behaviour. */}
        {hasResults && response && (
          <FilterRow>
            <FilterSelect
              aria-label="Sort results"
              value={sortCombined}
              onChange={(e) => handleSortChange(e.target.value)}
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </FilterSelect>
            {response.filters?.map((filter) => (
              <FilterSelect
                key={filter.param}
                aria-label={filter.label}
                value={filterValues[filter.param as FilterParamName] || ''}
                onChange={(e) =>
                  handleFilterChange(filter.param as FilterParamName, e.target.value)
                }
              >
                <option value="">{filter.label}</option>
                {filter.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </FilterSelect>
            ))}
          </FilterRow>
        )}

        {loading ? (
          <Loading>Searching&hellip;</Loading>
        ) : hasResults && response ? (
          <>
            <ResultsList>
              {response.results.map((r) => (
                <ResultCard key={r.id} href={r.url}>
                  <ResultTopRow>
                    {r.status_variant && r.status_label ? (
                      <StatusBadge $variant={r.status_variant}>{r.status_label}</StatusBadge>
                    ) : <span />}
                    <DateText>{formatDate(r.date)}</DateText>
                  </ResultTopRow>
                  <ResultTitle>
                    {renderSegments(r.title_segments, r.title || '(Untitled)')}
                  </ResultTitle>
                  {(r.byline || r.publication_name) && (
                    <ResultMeta>
                      {r.byline && <>By {r.byline}</>}
                      {r.byline && r.publication_name && <span className="dot">&middot;</span>}
                      {r.publication_name}
                    </ResultMeta>
                  )}
                  {r.excerpt_segments && r.excerpt_segments.length > 0 && (
                    <ResultExcerpt>{renderSegments(r.excerpt_segments)}</ResultExcerpt>
                  )}
                </ResultCard>
              ))}
            </ResultsList>
            {response.total_pages > 1 && (
              <Pagination>
                <PageLink
                  $disabled={response.current_page <= 1}
                  onClick={(e) => { e.preventDefault(); handlePageChange(response.current_page - 1); }}
                  href="#"
                >
                  &lsaquo; Prev
                </PageLink>
                {getVisiblePages(response.current_page, response.total_pages).map((p, i) =>
                  p === '...' ? (
                    <Ellipsis key={`ellipsis-${i}`}>&hellip;</Ellipsis>
                  ) : (
                    <PageLink
                      key={p}
                      $active={p === response.current_page}
                      onClick={(e) => { e.preventDefault(); handlePageChange(p as number); }}
                      href="#"
                    >
                      {p}
                    </PageLink>
                  )
                )}
                <PageLink
                  $disabled={response.current_page >= response.total_pages}
                  onClick={(e) => { e.preventDefault(); handlePageChange(response.current_page + 1); }}
                  href="#"
                >
                  Next &rsaquo;
                </PageLink>
              </Pagination>
            )}
          </>
        ) : null}
      </ContentWrapper>
    </PageContainer>
  );
}

export default SearchPage;
